import type { ButtonHTMLAttributes } from 'react';
import { forwardRef } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../../lib/utils';

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium',
    'transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    'disabled:pointer-events-none disabled:opacity-50',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
  ].join(' '),
  {
    variants: {
      variant: {
        default: [
          'bg-primary text-primary-foreground shadow-2xs',
          'hover:bg-primary/90',
          'focus-visible:ring-primary/40',
          'data-[active=true]:bg-primary/90',
        ].join(' '),
        secondary: [
          'bg-secondary text-secondary-foreground shadow-2xs',
          'hover:bg-secondary/80',
          'focus-visible:ring-primary/35',
          'data-[active=true]:bg-secondary/80',
        ].join(' '),
        outline: [
          'border border-input bg-background text-foreground shadow-2xs',
          'hover:bg-muted/50 hover:text-foreground',
          'focus-visible:ring-primary/35',
          'data-[active=true]:bg-muted/60 data-[active=true]:text-foreground',
        ].join(' '),
        ghost: [
          'text-foreground',
          'hover:bg-muted hover:text-foreground',
          'focus-visible:ring-primary/30',
          'data-[active=true]:bg-muted',
        ].join(' '),
        destructive: [
          'bg-destructive text-destructive-foreground shadow-2xs',
          'hover:bg-destructive/90',
          'focus-visible:ring-destructive/45',
          'data-[active=true]:bg-destructive/90',
        ].join(' '),
        link: [
          'text-primary',
          'underline-offset-4 hover:underline',
          'focus-visible:ring-primary/35 focus-visible:ring-offset-0',
          'data-[active=true]:underline',
        ].join(' '),
      },
      size: {
        default: 'h-9 px-4 py-2 [&_svg]:size-4',
        sm: 'h-8 rounded-md px-3 text-xs [&_svg]:size-4',
        lg: 'h-10 rounded-md px-8 [&_svg]:size-4',
        icon: 'size-9 gap-0 p-0 [&_svg]:size-4',
        'icon-sm': 'size-8 gap-0 p-0 [&_svg]:size-4',
        'icon-lg': 'size-10 gap-0 p-0 [&_svg]:size-4',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

function ButtonSpinner({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn('size-4 animate-spin', className)}
      aria-hidden
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  );
}

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /** Shows spinner, sets `aria-busy`, and disables interaction (Figma loading state). */
  loading?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, asChild = false, loading = false, disabled, children, ...props },
    ref
  ) => {
    const isDisabled = Boolean(disabled || loading);
    const useAsChild = asChild && !loading;

    const Comp = useAsChild ? Slot : 'button';

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }), loading && 'cursor-wait gap-2')}
        ref={ref}
        disabled={isDisabled}
        aria-busy={loading || undefined}
        data-loading={loading ? '' : undefined}
        {...props}
      >
        {loading ? (
          <>
            <ButtonSpinner
              className={size === 'sm' || size === 'icon-sm' ? 'size-3.5' : undefined}
            />
            {children}
          </>
        ) : (
          children
        )}
      </Comp>
    );
  }
);
Button.displayName = 'Button';

export { Button, ButtonSpinner, buttonVariants };
